import { useState, useRef, useEffect } from "react";
import { MONTHS } from "../../constants/data";
import { IconSearch, IconTrend } from "../icons/Icons";
import DashboardView from "./DashboardView";

/** Returns 'cab', 'chassis', or 'other' based on aktuelle_station value */
function classifyStation(station) {
  if (!station) return "other";
  const s = station.toLowerCase();
  if (s.includes("cab")) return "cab";
  if (s.includes("chassis")) return "chassis";
  return "other";
}

const FILTER_OPTIONS = [
  { value: "both",    label: "Both"    },
  { value: "chassis", label: "Chassis" },
  { value: "cab",     label: "Cab"     },
];

export default function YearView({ year, onYearChange, allEvents, vehicles, projectColorMap = {}, onSelectVehicle }) {
  const [filter, setFilter] = useState("both");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Header view toggle: "year" (calendar grid) ↔ "dashboard" (analytics).
  // The calendar state/data below is untouched; dashboard is an additional view.
  const [viewMode, setViewMode] = useState("year");
  const dropdownRef = useRef(null);

  // Search state: list of committed tags + current input text
  const [searchTags, setSearchTags] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  /** Commit the current input as a search tag */
  function commitSearch(raw) {
    const val = raw.trim().toLowerCase();
    if (val && !searchTags.includes(val)) {
      setSearchTags(prev => [...prev, val]);
    }
    setSearchInput("");
  }

  function handleSearchKeyDown(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitSearch(searchInput);
    } else if (e.key === "Backspace" && searchInput === "" && searchTags.length > 0) {
      setSearchTags(prev => prev.slice(0, -1));
    }
  }

  function removeTag(tag) {
    setSearchTags(prev => prev.filter(t => t !== tag));
  }

  // Filter events by chassis/cab selection
  const afterTypeFilter = filter === "both"
    ? allEvents
    : allEvents.filter(e => classifyStation(e._aktuelle_station) === filter);

  // Filter events by search tags (match against fahrzeugnummer / label)
  const filteredEvents = searchTags.length === 0
    ? afterTypeFilter
    : afterTypeFilter.filter(e => {
        const fzg = e._fahrzeugnummer || "";
        const lbl = (e.label || "").toLowerCase();
        return searchTags.some(tag => fzg.includes(tag) || lbl.includes(tag));
      });

  // Build a dynamic event map from filtered events
  const dynEvMap = {};
  filteredEvents.forEach(e => { dynEvMap[`${e.row}-${e.col}`] = e; });

  const calRows = Array.from({ length: 31 }, (_, i) => i + 1).map(row => ({
    row,
    cells: Array.from({ length: 12 }, (_, ci) => dynEvMap[`${row}-${ci + 1}`] || null),
  }));

  const selectedLabel = FILTER_OPTIONS.find(o => o.value === filter)?.label ?? "Both";

  return (
    <div style={{ flex: 1, padding: 12, display: "flex", flexDirection: "column", gap: 10, overflowY: "auto" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        {/* Chassis vs. Cab dropdown + vehicle search — always visible in both views */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

          {/* Chassis vs. Cab dropdown */}
          <div ref={dropdownRef} style={{ position: "relative" }}>
            <div
              onClick={() => setDropdownOpen(o => !o)}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "white", border: "1px solid #ccc", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, userSelect: "none", minWidth: 130 }}
            >
              <span style={{ flex: 1 }}>{filter === "both" ? "Chassis vs. Cab" : selectedLabel}</span>
              <svg width={10} height={10} viewBox="0 0 10 10" fill="currentColor" style={{ transform: dropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
                <path d="M2 3l3 4 3-4"/>
              </svg>
            </div>
            {dropdownOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, background: "white", border: "1px solid #ccc", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", zIndex: 100, minWidth: "100%", overflow: "hidden" }}>
                {FILTER_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    onClick={() => { setFilter(opt.value); setDropdownOpen(false); }}
                    style={{
                      padding: "8px 14px", fontSize: 12, cursor: "pointer",
                      background: filter === opt.value ? "#e8f4f4" : "white",
                      color: filter === opt.value ? "#1e6b6b" : "#333",
                      fontWeight: filter === opt.value ? 600 : 400,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = filter === opt.value ? "#e8f4f4" : "#f5f5f5"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = filter === opt.value ? "#e8f4f4" : "white"; }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Multi-vehicle search */}
          <div
            ref={searchRef}
            onClick={() => searchRef.current.querySelector("input")?.focus()}
            style={{
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4,
              background: "white", border: "1px solid #ccc", borderRadius: 6,
              padding: "3px 8px", cursor: "text", fontSize: 12, minWidth: 200, maxWidth: 380,
            }}
          >
            {searchTags.map(tag => (
              <span key={tag} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: "#1e6b6b", color: "white", borderRadius: 4,
                padding: "1px 7px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
              }}>
                {tag}
                <span
                  onClick={e => { e.stopPropagation(); removeTag(tag); }}
                  style={{ cursor: "pointer", fontSize: 13, lineHeight: 1, opacity: 0.8 }}
                >×</span>
              </span>
            ))}
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onBlur={() => commitSearch(searchInput)}
              placeholder={searchTags.length === 0 ? "Search vehicle no. (Enter to add)…" : ""}
              style={{
                border: "none", outline: "none", fontSize: 12, flex: 1, minWidth: 80,
                padding: "2px 0", background: "transparent", color: "#333",
              }}
            />
            {(searchTags.length > 0 || searchInput) && (
              <span
                onClick={() => { setSearchTags([]); setSearchInput(""); }}
                title="Clear search"
                style={{ cursor: "pointer", color: "#aaa", fontSize: 14, lineHeight: 1, padding: "0 2px" }}
              >×</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Year navigation — only relevant to the Calendar view, hidden in Dashboard mode */}
          {viewMode === "year" && (
            <>
              <button onClick={() => onYearChange(-1)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#555", padding: "3px 8px", borderRadius: 4 }}>‹</button>
              <div style={{ fontSize: 20, fontWeight: 500, display: "flex", alignItems: "center", gap: 7 }}>{year} <IconTrend /></div>
              <button onClick={() => onYearChange(1)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#555", padding: "3px 8px", borderRadius: 4 }}>›</button>
            </>
          )}
          {/* View toggle: Calendar ↔ Dashboard (styled to match the toolbar controls) */}
          <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 6, overflow: "hidden", userSelect: "none" }}>
            <button
              onClick={() => setViewMode("year")}
              style={{
                border: "none", cursor: "pointer", padding: "5px 12px", fontSize: 12,
                fontWeight: viewMode === "year" ? 600 : 500,
                background: viewMode === "year" ? "#1e6b6b" : "white",
                color: viewMode === "year" ? "white" : "#555",
                transition: "background 0.15s, color 0.15s",
              }}
            >Calendar</button>
            <button
              onClick={() => setViewMode("dashboard")}
              style={{
                border: "none", borderLeft: "1px solid #ccc", cursor: "pointer", padding: "5px 12px", fontSize: 12,
                fontWeight: viewMode === "dashboard" ? 600 : 500,
                background: viewMode === "dashboard" ? "#1e6b6b" : "white",
                color: viewMode === "dashboard" ? "white" : "#555",
                transition: "background 0.15s, color 0.15s",
              }}
            >Dashboard</button>
          </div>
        </div>
      </div>

      {/* Calendar table — shown only in Year-wise mode (unchanged) */}
      {viewMode === "year" && (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", background: "white", borderRadius: 6, fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ background: "#1e6b6b", color: "white", width: 28, padding: "7px 3px", fontSize: 12 }}></th>
              {MONTHS.map(m => (
                <th key={m} style={{ background: "#1e6b6b", color: "white", padding: "7px 3px", fontSize: 12, fontWeight: 600, textAlign: "center" }}>{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calRows.map(({ row, cells }) => (
              <tr key={row}>
                <td style={{ background: "#eaf0ef", textAlign: "center", color: "#888", fontSize: 10, fontWeight: 500, border: "0.5px solid #dde3e8", height: 21 }}>{row}</td>
                {cells.map((ev, ci) => (
                  <td key={ci} style={{ border: "0.5px solid #dde3e8", height: 21, padding: "1px 3px", verticalAlign: "middle", background: row % 2 === 0 ? "#f4f8f8" : "white" }}>                    {ev && (() => {
                        const bg    = projectColorMap[ev._projektname] || "#e0e0e0";
                        // derive a readable text colour: darken the bg slightly
                        const color = "#1a1a2e";
                        return (                          <span
                            onClick={() => {
                              const v = vehicles.find(v => v.id === ev._vehicleId);
                              if (v) onSelectVehicle(v);
                            }}
                            title={ev._aktuelle_station ? `${ev._projektname || ev.label} · ${ev._aktuelle_station}` : (ev._projektname || ev.label)}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 3,
                              borderRadius: 3, padding: "1px 5px", fontSize: 10, fontWeight: 600,
                              whiteSpace: "nowrap", cursor: ev._vehicleId ? "pointer" : "default",
                              background: bg, color,
                            }}
                          >
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, opacity: 0.45, flexShrink: 0 }} />
                            {ev.label}
                          </span>
                        );
                      })()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Dashboard view — placeholder for now; the month-wise vehicle count
          chart is added in the next step. Rendered in place of the grid. */}
      {viewMode === "dashboard" && <DashboardView />}
    </div>
  );
}
